import dayjs, { type Dayjs } from 'dayjs';
import i18next from 'i18next';
import { parseDateLimit } from './date-limits';
type FieldDefinition = {
  name: string;
  type?: string;
  options?: Array<string | { label: string; value: string }>;
  min?: string | number;
  max?: string | number;
  autoFillValue?: unknown;
  addressPart?: string;
};

function getFirstOptionValue(
  options: Array<string | { label: string; value: string }>,
): string | undefined {
  const first = options[0];
  if (first === undefined) return undefined;
  return typeof first === 'string' ? first : first.value;
}

const dateFormats = {
  date: 'YYYY-MM-DD',
  datetime: 'YYYY-MM-DDTHH:mm',
  month: 'YYYY-MM',
} as const;

function resolveBound(bound?: string | number): Dayjs | undefined {
  const parsed = parseDateLimit(bound === undefined ? undefined : String(bound));
  return parsed?.isValid() ? parsed : undefined;
}

function getClampedDateValue(
  type: 'date' | 'datetime' | 'month',
  min?: string | number,
  max?: string | number,
): string {
  return clampToBounds(dayjs(), min, max).format(dateFormats[type]);
}

function clampToBounds(value: Dayjs, min?: string | number, max?: string | number): Dayjs {
  const minimum = resolveBound(min);
  const maximum = resolveBound(max);
  if (minimum && value.isBefore(minimum)) return minimum;
  if (maximum && value.isAfter(maximum)) return maximum;
  return value;
}

/**
 * Auto-fills form fields by reading the HTML placeholder attribute from rendered inputs.
 * For select/radio fields, picks the first option value since they don't have placeholders.
 */
export function autoFillFromPlaceholders(
  fieldNames: string[],
  setValue: (
    name: string,
    value: unknown,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void,
  fields?: FieldDefinition[],
) {
  const fieldMap = new Map<string, FieldDefinition>();
  if (fields) {
    for (const field of fields) {
      fieldMap.set(field.name, field);
    }
  }

  for (const name of fieldNames) {
    // Look up by full name first, then by base name (handles nested names like "assets.0.fieldName")
    const baseName = name.split('.').pop() ?? name;
    const lowerBaseName = baseName.toLowerCase();
    const fieldDef = fieldMap.get(name) ?? fieldMap.get(baseName);

    if (fieldDef?.autoFillValue !== undefined) {
      setValue(name, fieldDef.autoFillValue, {
        shouldDirty: true,
        shouldValidate: true,
      });
      continue;
    }

    if (
      fieldDef &&
      (fieldDef.type === 'select' || fieldDef.type === 'radio') &&
      fieldDef.options?.length
    ) {
      const firstValue = getFirstOptionValue(fieldDef.options);
      if (firstValue) {
        setValue(name, firstValue, {
          shouldDirty: true,
          shouldValidate: true,
        });
        continue;
      }
    }

    if (
      fieldDef &&
      (fieldDef.type === 'date' || fieldDef.type === 'datetime' || fieldDef.type === 'month')
    ) {
      const isBirthdate =
        lowerBaseName.includes('birthdate') || lowerBaseName.includes('dateofbirth');
      const isStartedAt = baseName === 'startedAt';
      let value: string;
      if (isBirthdate) {
        value = dayjs().subtract(30, 'year').format(dateFormats.date);
      } else if (isStartedAt && fieldDef.type === 'datetime') {
        // pushed later when the field minimum sits beyond 15 minutes
        value = clampToBounds(dayjs().add(15, 'minute'), fieldDef.min, fieldDef.max).format(
          dateFormats.datetime,
        );
      } else {
        value = getClampedDateValue(fieldDef.type, fieldDef.min, fieldDef.max);
      }
      setValue(name, value, {
        shouldDirty: true,
        shouldValidate: true,
      });
      continue;
    }

    if (fieldDef?.type === 'company') {
      setValue(
        name,
        { name: 'KORINT' },
        {
          shouldDirty: true,
          shouldValidate: true,
        },
      );
      continue;
    }

    if (fieldDef?.type === 'multiSelect' && fieldDef.options?.length) {
      const firstValue = getFirstOptionValue(fieldDef.options);
      if (firstValue) {
        setValue(name, [firstValue], {
          shouldDirty: true,
          shouldValidate: true,
        });
        continue;
      }
    }

    // Country selects option values are display names in the UI language
    if (fieldDef?.addressPart === 'country') {
      const france = new Intl.DisplayNames([i18next.language ?? 'fr'], { type: 'region' }).of('FR');
      setValue(name, france, {
        shouldDirty: true,
        shouldValidate: true,
      });
      continue;
    }

    if (lowerBaseName.includes('registrationnumber')) {
      setValue(name, 'AA123BC', {
        shouldDirty: true,
        shouldValidate: true,
      });
      continue;
    }

    const input = document.querySelector<HTMLInputElement | HTMLTextAreaElement>(
      `[name="${name}"]`,
    );
    const placeholder = input?.placeholder;
    if (placeholder) {
      setValue(name, placeholder, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }
}
